import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import SectionHeading from '../../components/common/SectionHeading';
import useApi from '../../hooks/useApi';
import { getNoticeAuthorDisplay } from '../../utils/noticeAuthor';

const PORTAL_USER_KEY = 'portal_user';
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5003';

const Wrapper = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: ${({ theme }) => theme.shadows.md};

  @media (max-width: 900px) {
    border-radius: 1rem;
    padding: 1rem;
  }
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: 1rem;
  font-size: 0.88rem;
  font-weight: 700;
  color: #4f46e5;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Article = styled.article`
  margin-top: 0.5rem;
  padding: 1.25rem;
  border-radius: 1rem;
  border: 1px solid #e2e8f0;
  background: #fff;
`;

const VideoWrapper = styled.div`
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  border-radius: 0.85rem;
  background: #000;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const Title = styled.h2`
  margin: 1rem 0 0.35rem;
  font-size: 1.2rem;
  color: #0f172a;
  line-height: 1.35;
`;

const StudentName = styled.p`
  margin: 0 0 0.5rem;
  font-size: 0.9rem;
  font-weight: 700;
  color: #6366f1;
`;

const EventLink = styled(Link)`
  display: inline-block;
  margin-bottom: 0.75rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: #64748b;
  text-decoration: none;
  &:hover {
    color: #4f46e5;
    text-decoration: underline;
  }
`;

const MetaLine = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.82rem;
  color: #64748b;
`;

const Description = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.92rem;
  line-height: 1.6;
  color: #334155;
`;

const LikeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const LikeButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid ${({ $active }) => ($active ? '#fda4af' : '#e2e8f0')};
  background: ${({ $active }) => ($active ? '#fff1f2' : '#fff')};
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  font-size: 0.86rem;
  font-weight: 700;
  cursor: pointer;
  color: ${({ $active }) => ($active ? '#e11d48' : '#64748b')};
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const CommentsSection = styled.section`
  margin-top: 1.75rem;
  padding-top: 1.35rem;
  border-top: 1px solid #e8ecf1;
`;

const CommentsTitle = styled.h3`
  margin: 0 0 0.85rem;
  font-size: 0.95rem;
  font-weight: 800;
  color: #0f172a;
`;

const CommentForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin-top: 0.85rem;
  padding: 1rem;
  border-radius: 0.75rem;
  border: 1px solid #e8ecf1;
  background: #fafbfc;
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 88px;
  border: 1px solid #d8dee9;
  border-radius: 0.6rem;
  padding: 0.6rem 0.7rem;
  font-size: 0.9rem;
  resize: vertical;
  background: #fff;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

const PrimaryButton = styled.button`
  border: none;
  border-radius: 0.6rem;
  padding: 0.45rem 0.9rem;
  font-weight: 700;
  font-size: 0.86rem;
  cursor: pointer;
  background: rgba(99, 102, 241, 0.95);
  color: #f8fafc;
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const GhostButton = styled.button`
  border: 1px solid #d8dee9;
  border-radius: 0.5rem;
  padding: 0.32rem 0.65rem;
  font-weight: 600;
  font-size: 0.78rem;
  cursor: pointer;
  background: #fff;
  color: #64748b;
`;

const DangerButton = styled.button`
  border: 1px solid #fecaca;
  background: #fef2f2;
  border-radius: 0.5rem;
  padding: 0.32rem 0.65rem;
  font-weight: 700;
  font-size: 0.78rem;
  cursor: pointer;
  color: #b91c1c;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CommentList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const CommentInner = styled.div`
  padding-left: ${({ $depth }) => `${Math.min($depth, 6) * 0.85}rem`};
  border-left: ${({ $depth }) => ($depth > 0 ? '2px solid #e0e7ff' : 'none')};
`;

const CommentCard = styled.div`
  border: 1px solid #e8ecf1;
  border-radius: 0.75rem;
  background: #fafbfc;
  padding: 0.85rem 1rem;
`;

const CommentGrid = styled.div`
  display: grid;
  grid-template-columns: 2.35rem minmax(0, 1fr) auto;
  grid-template-areas:
    'cgav cgauth cgaside'
    'cgav cgbody cgbody';
  column-gap: 0.65rem;
  row-gap: 0.35rem;
`;

const SmallAvatar = styled.span`
  width: 2.35rem;
  height: 2.35rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  background: #fff;
`;

const AuthorName = styled.span`
  font-weight: 800;
  color: #0f172a;
  font-size: 0.875rem;
`;

const AuthorLogin = styled.span`
  font-size: 0.72rem;
  color: #64748b;
`;

const CommentGridBody = styled.div`
  grid-area: cgbody;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.875rem;
  line-height: 1.55;
  color: ${({ $removed }) => ($removed ? '#94a3b8' : '#334155')};
  font-style: ${({ $removed }) => ($removed ? 'italic' : 'normal')};
`;

const CommentDate = styled.span`
  font-size: 0.72rem;
  color: #94a3b8;
`;

const InlineReplyBox = styled.form`
  margin-top: 0.65rem;
  padding: 0.75rem 0.85rem;
  border-radius: 0.65rem;
  border: 1px solid #c7d2fe;
  background: #f8f9ff;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const InlineReplyTitle = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  color: #4f46e5;
`;

const CompactTextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 72px;
  border: 1px solid #d8dee9;
  border-radius: 0.55rem;
  padding: 0.5rem 0.65rem;
  font-size: 0.875rem;
  resize: vertical;
  background: #fff;
`;

const ErrorText = styled.p`
  margin: 0.75rem 0 0;
  color: #b91c1c;
  font-size: 0.9rem;
`;

const EmptyComments = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 0.88rem;
`;

const readPortalUser = () => {
  try {
    const raw = sessionStorage.getItem(PORTAL_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.login_id ? parsed : null;
  } catch {
    return null;
  }
};

const formatDt = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' });
};

const buildCommentTree = (flat) => {
  const byParent = new Map();
  flat.forEach((c) => {
    const pid = c.parent_comment_id ?? null;
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid).push(c);
  });
  const walk = (parentId, depth) => {
    const kids = byParent.get(parentId) || [];
    return kids.flatMap((node) => [{ node, depth }, ...walk(node.id, depth + 1)]);
  };
  return walk(null, 0);
};

const canCommentPresentation = (roles = []) =>
  ['student', 'parent', 'teacher', 'admin', 'super_admin'].some((r) => roles.includes(r));

const isBoardModerator = (roles = []) =>
  roles.includes('teacher') || roles.includes('admin') || roles.includes('super_admin');

const samePortalUserId = (portalUserId, authorUserId) => {
  if (portalUserId == null || authorUserId == null) return false;
  return Number(portalUserId) === Number(authorUserId);
};

const PortalPresentationWorkPage = () => {
  const { eventId, workId } = useParams();
  const { request, loading } = useApi();
  const portalUser = useMemo(() => readPortalUser(), []);
  const [work, setWork] = useState(null);
  const [comments, setComments] = useState([]);
  const [error, setError] = useState('');
  const [commentText, setCommentText] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [commentError, setCommentError] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentDraft, setEditCommentDraft] = useState('');
  const viewCountGateRef = useRef({ workKey: '', counted: false, pendingBump: false });
  const detailFetchGenRef = useRef(0);

  const canComment = canCommentPresentation(portalUser?.roles || []);
  const moderator = isBoardModerator(portalUser?.roles || []);

  const load = useCallback(
    async ({ recordView = true } = {}) => {
      if (!portalUser?.login_id || !workId) return;
      const fetchGen = ++detailFetchGenRef.current;
      let bumpView = recordView;
      let lockedPendingBump = false;
      if (bumpView) {
        const key = `${workId}:${portalUser.login_id}`;
        const g = viewCountGateRef.current;
        if (g.workKey !== key) {
          viewCountGateRef.current = { workKey: key, counted: false, pendingBump: false };
        }
        const g2 = viewCountGateRef.current;
        if (g2.counted || g2.pendingBump) {
          bumpView = false;
        } else {
          g2.pendingBump = true;
          lockedPendingBump = true;
        }
      }
      setError('');
      try {
        const qs = new URLSearchParams({
          login_id: portalUser.login_id,
          record_view: bumpView ? 'true' : 'false',
        });
        const data = await request({
          endpoint: `${BACKEND_BASE_URL}/api/presentation-works/${encodeURIComponent(workId)}?${qs.toString()}`,
        });
        if (fetchGen !== detailFetchGenRef.current) return;
        setWork(data?.work || null);
        setComments(data?.comments || []);
        if (bumpView) {
          viewCountGateRef.current.counted = true;
        }
      } catch (e) {
        if (fetchGen !== detailFetchGenRef.current) return;
        setWork(null);
        setComments([]);
        setError(e?.responseData?.message || '작품을 불러오지 못했습니다.');
      } finally {
        if (lockedPendingBump) {
          viewCountGateRef.current.pendingBump = false;
        }
      }
    },
    [portalUser?.login_id, request, workId],
  );

  useEffect(() => {
    load({ recordView: true });
  }, [load]);

  const threaded = useMemo(() => buildCommentTree(comments), [comments]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    setCommentError('');
    const text = commentText.trim();
    if (!text) return;
    try {
      await request({
        endpoint: `${BACKEND_BASE_URL}/api/presentation-works/${encodeURIComponent(workId)}/comments`,
        method: 'POST',
        body: {
          login_id: portalUser.login_id,
          content: text,
          parent_comment_id: replyTo,
        },
      });
      setCommentText('');
      setReplyTo(null);
      await load({ recordView: false });
    } catch (err) {
      setCommentError(err?.responseData?.message || '댓글 등록에 실패했습니다.');
    }
  };

  const beginCommentEdit = (c) => {
    setCommentError('');
    setEditingCommentId(c.id);
    setEditCommentDraft(c.content || '');
    setReplyTo(null);
    setCommentText('');
  };

  const cancelCommentEdit = () => {
    setEditingCommentId(null);
    setEditCommentDraft('');
    setCommentError('');
  };

  const handleSaveComment = async (e, commentId) => {
    e.preventDefault();
    setCommentError('');
    const text = editCommentDraft.trim();
    if (!text) return;
    try {
      await request({
        method: 'PUT',
        endpoint: `${BACKEND_BASE_URL}/api/presentation-works/comments/${encodeURIComponent(commentId)}`,
        body: { login_id: portalUser.login_id, content: text },
      });
      setEditingCommentId(null);
      setEditCommentDraft('');
      await load({ recordView: false });
    } catch (err) {
      setCommentError(err?.responseData?.message || '댓글 수정에 실패했습니다.');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('이 댓글을 삭제할까요?')) return;
    setCommentError('');
    try {
      await request({
        method: 'DELETE',
        endpoint: `${BACKEND_BASE_URL}/api/presentation-works/comments/${encodeURIComponent(commentId)}`,
        body: { login_id: portalUser.login_id },
      });
      if (editingCommentId === commentId) {
        setEditingCommentId(null);
        setEditCommentDraft('');
      }
      await load({ recordView: false });
    } catch (err) {
      setCommentError(err?.responseData?.message || '댓글 삭제에 실패했습니다.');
    }
  };

  const handleModerateComment = async (commentId) => {
    if (!window.confirm('이 댓글을 삭제 처리할까요?')) return;
    setCommentError('');
    try {
      await request({
        endpoint: `${BACKEND_BASE_URL}/api/presentation-works/comments/${encodeURIComponent(commentId)}/moderate`,
        method: 'POST',
        body: { login_id: portalUser.login_id },
      });
      if (editingCommentId === commentId) {
        setEditingCommentId(null);
        setEditCommentDraft('');
      }
      await load({ recordView: false });
    } catch (err) {
      setCommentError(err?.responseData?.message || '처리에 실패했습니다.');
    }
  };

  const handleToggleLike = async () => {
    if (!work) return;
    setCommentError('');
    try {
      const data = await request({
        method: 'POST',
        endpoint: `${BACKEND_BASE_URL}/api/presentation-works/${encodeURIComponent(workId)}/like`,
        body: { login_id: portalUser.login_id },
      });
      detailFetchGenRef.current += 1;
      setWork((prev) =>
        prev
          ? {
              ...prev,
              like_count: Number.isFinite(Number(data?.like_count)) ? Number(data.like_count) : prev.like_count,
              liked_by_me: Boolean(data?.liked_by_me),
            }
          : prev,
      );
    } catch (err) {
      setCommentError(err?.responseData?.message || '좋아요 처리에 실패했습니다.');
    }
  };

  if (!work && !error) {
    return (
      <Wrapper>
        <BackLink to={`/portal/presentations/${eventId}`}>← 작품 목록</BackLink>
        <SectionHeading eyebrow="PRESENTATION" title="작품 상세" />
        <p style={{ color: '#64748b' }}>{loading ? '불러오는 중...' : ''}</p>
      </Wrapper>
    );
  }

  if (error && !work) {
    return (
      <Wrapper>
        <BackLink to={`/portal/presentations/${eventId}`}>← 작품 목록</BackLink>
        <SectionHeading eyebrow="PRESENTATION" title="작품 상세" />
        <ErrorText>{error}</ErrorText>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <BackLink to={`/portal/presentations/${eventId}`}>← 작품 목록</BackLink>
      <SectionHeading eyebrow="PRESENTATION" title="작품 상세" />

      <Article>
        {work.event?.title ? (
          <EventLink to={`/portal/presentations/${eventId}`}>{work.event.title}</EventLink>
        ) : null}
        <VideoWrapper>
          {work.youtube_video_id ? (
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${work.youtube_video_id}`}
              title={work.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
            />
          ) : null}
        </VideoWrapper>
        <Title>{work.title}</Title>
        {work.student_name ? <StudentName>{work.student_name}</StudentName> : null}
        <MetaLine>조회 {work.view_count ?? 0}</MetaLine>
        {work.description ? <Description>{work.description}</Description> : null}
        <LikeRow>
          <LikeButton
            type="button"
            $active={Boolean(work.liked_by_me)}
            disabled={loading}
            onClick={handleToggleLike}
            aria-pressed={Boolean(work.liked_by_me)}
          >
            <span aria-hidden>{work.liked_by_me ? '♥' : '♡'}</span>
            <span>좋아요</span>
            <span>{work.like_count ?? 0}</span>
          </LikeButton>
        </LikeRow>
      </Article>

      <CommentsSection>
        <CommentsTitle>댓글 {comments.length}</CommentsTitle>

        {threaded.length === 0 ? (
          <EmptyComments>첫 댓글을 남겨 보세요.</EmptyComments>
        ) : (
          <CommentList>
            {threaded.map(({ node: c, depth }) => {
              const isCAuthor = samePortalUserId(portalUser?.id, c.author?.user_id);
              const { primary, secondary } = getNoticeAuthorDisplay(c.author);
              return (
                <li key={c.id}>
                  <CommentInner $depth={depth}>
                    <CommentCard>
                      <CommentGrid>
                        <div style={{ gridArea: 'cgav' }}>
                          <SmallAvatar aria-hidden>{c.author?.profile_emoji || '🙂'}</SmallAvatar>
                        </div>
                        <div style={{ gridArea: 'cgauth', minWidth: 0 }}>
                          <AuthorName>{primary}</AuthorName>
                          {secondary ? <AuthorLogin>{secondary}</AuthorLogin> : null}
                        </div>
                        <div
                          style={{
                            gridArea: 'cgaside',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '0.4rem',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <CommentDate>{formatDt(c.created_at)}</CommentDate>
                          {isCAuthor && !c.is_moderation_removed && editingCommentId !== c.id ? (
                            <>
                              <GhostButton type="button" disabled={loading} onClick={() => beginCommentEdit(c)}>
                                수정
                              </GhostButton>
                              <GhostButton type="button" disabled={loading} onClick={() => handleDeleteComment(c.id)}>
                                삭제
                              </GhostButton>
                            </>
                          ) : null}
                          {moderator && !isCAuthor && !c.is_moderation_removed ? (
                            <DangerButton type="button" disabled={loading} onClick={() => handleModerateComment(c.id)}>
                              삭제
                            </DangerButton>
                          ) : null}
                          {canComment && !c.is_moderation_removed && !editingCommentId ? (
                            <GhostButton
                              type="button"
                              onClick={() => {
                                setEditingCommentId(null);
                                setEditCommentDraft('');
                                setReplyTo(c.id);
                                setCommentText('');
                                setCommentError('');
                              }}
                            >
                              답글
                            </GhostButton>
                          ) : null}
                        </div>
                        {editingCommentId === c.id ? (
                          <span style={{ gridArea: 'cgbody' }} aria-hidden />
                        ) : (
                          <CommentGridBody $removed={c.is_moderation_removed}>{c.content}</CommentGridBody>
                        )}
                      </CommentGrid>
                      {editingCommentId === c.id ? (
                        <InlineReplyBox onSubmit={(e) => handleSaveComment(e, c.id)}>
                          <InlineReplyTitle>댓글 수정</InlineReplyTitle>
                          <CompactTextArea
                            value={editCommentDraft}
                            onChange={(ev) => setEditCommentDraft(ev.target.value)}
                            maxLength={4000}
                            disabled={loading}
                          />
                          <ButtonRow>
                            <PrimaryButton type="submit" disabled={loading}>
                              저장
                            </PrimaryButton>
                            <GhostButton type="button" disabled={loading} onClick={cancelCommentEdit}>
                              취소
                            </GhostButton>
                          </ButtonRow>
                        </InlineReplyBox>
                      ) : null}
                    </CommentCard>
                    {replyTo === c.id && canComment && !c.is_moderation_removed && !editingCommentId ? (
                      <InlineReplyBox onSubmit={handleCommentSubmit}>
                        <InlineReplyTitle>답글 작성</InlineReplyTitle>
                        <CompactTextArea
                          value={commentText}
                          onChange={(ev) => setCommentText(ev.target.value)}
                          disabled={loading}
                        />
                        <ButtonRow>
                          <PrimaryButton type="submit" disabled={loading}>
                            답글 등록
                          </PrimaryButton>
                          <GhostButton
                            type="button"
                            onClick={() => {
                              setReplyTo(null);
                              setCommentText('');
                              setCommentError('');
                            }}
                          >
                            취소
                          </GhostButton>
                        </ButtonRow>
                      </InlineReplyBox>
                    ) : null}
                  </CommentInner>
                </li>
              );
            })}
          </CommentList>
        )}

        {canComment && !replyTo && !editingCommentId ? (
          <CommentForm onSubmit={handleCommentSubmit}>
            <TextArea
              value={commentText}
              onChange={(ev) => setCommentText(ev.target.value)}
              placeholder="댓글을 입력하세요."
              disabled={loading}
              maxLength={4000}
            />
            <ButtonRow>
              <PrimaryButton type="submit" disabled={loading}>
                댓글 등록
              </PrimaryButton>
            </ButtonRow>
            {commentError ? <ErrorText style={{ margin: 0 }}>{commentError}</ErrorText> : null}
          </CommentForm>
        ) : null}
        {commentError && (replyTo || editingCommentId) ? (
          <ErrorText>{commentError}</ErrorText>
        ) : null}
      </CommentsSection>
    </Wrapper>
  );
};

export default PortalPresentationWorkPage;
